import { message } from "antd";
import { stringify } from "csv-stringify/sync";
import saveAs from "file-saver";
import moment from "moment";
import { ChartDataType } from "../COREChart";

const allowCsvType: string[] = ["scatter", "scattergl", "bar", "lines"];

class CSV {
  xAxisTitle: string;
  chartType: string;
  rows: any;
  serieses: Set<string>;
  xs: Set<number>;
  charts: Set<string>;
  xAxisTitleType: string;

  constructor(xAxisTitle, chartType, xAxisTitleType) {
    this.xAxisTitle = xAxisTitle;
    this.xAxisTitleType = xAxisTitleType;
    this.chartType = chartType;
    this.rows = {};
    this.serieses = new Set();
    this.xs = new Set();
    this.charts = new Set(); // will hold the list of charts, eg "Jan", "Feb", etc, "Mon" "Tue"
  }

  addCell(x, y, series, chart = "default") {
    this.serieses.add(series);
    this.charts.add(chart);
    this.xs.add(x);
    if (!this.rows.hasOwnProperty(chart)) {
      this.rows[chart] = {};
    }
    if (!this.rows[chart].hasOwnProperty(x)) {
      this.rows[chart][x] = { x };
    }
    this.rows[chart][x][series] = y?.toString();
  }

  getSeriesCheck(chart, xValue) {
    const serieses = this.getSerieses();
    return serieses.map((s) => {
      if (!this.rows[chart].hasOwnProperty(xValue)) return "";
      if (!this.rows[chart][xValue].hasOwnProperty(s)) return "";
      return this.rows[chart][xValue][s] ?? "";
    });
  }

  // Generator<number, string, boolean>
  *getRows() {
    const charts = this.getCharts();
    const xs = this.getXs();
    switch (this.chartType) {
      case "week":
      case "month":
        for (const chart of charts) {
          for (const x of xs) {
            const formattedX = this.formatTime(x, formatAsTime(x));
            yield [chart, formattedX, ...this.getSeriesCheck(chart, x)];
          }
        }
        break;
      default:
        for (const chart of charts) {
          for (const x of xs) {
            const formattedX = this.formatTime(
              x,
              this.chartType === "day"
                ? formatAsTime(x)
                : moment.utc(x).format("DD/MM/YYYY HH:mm:ss")
            );
            yield [formattedX, ...this.getSeriesCheck(chart, x)];
          }
        }
        break;
    }
  }

  formatTime(x: number, formatter: string) {
    return this.xAxisTitleType === "category" ? x : formatter;
  }

  getSerieses() {
    return Array.from(this.serieses);
  }
  getCharts() {
    return Array.from(this.charts);
  }
  getXs() {
    return Array.from(this.xs).sort((front, next) => {
      // if (isFinite(parseInt(front))) return parseInt(front) - parseInt(next);
      if (isFinite(front)) return front - next;
      return moment.utc(front).valueOf() - moment.utc(next).valueOf();
    });
  }
  getTitles() {
    switch (this.chartType) {
      case "day":
        return ["Time", ...this.getSerieses()];
      case "week":
      case "month":
        return ["", "Time", ...this.getSerieses()];
      default:
        return [this.xAxisTitle, ...this.getSerieses()];
    }
  }

  getCSVArrayOfArrays() {
    return [this.getTitles(), ...this.getRows()];
  }
}

export const downloadFileName = (filename: string) => {
  return filename.replace(/<[^>]{1,500}>/gi, "");
};

export const downloadCSVFile = (
  chartData,
  isFormattedData = false,
  fileName = ""
) => {
  const formattedData: string[][] = isFormattedData
    ? chartData
    : formatChartDataToCsv(chartData);
  if (!formattedData) {
    return message.error("Something went wrong, please try again.");
  }
  const chartFileName = isFormattedData ? fileName : chartData.layout.filename;
  const output = stringify(formattedData);
  const csvString = output.replace(/\r?\n$/, "");
  // Byte Order Mark (BOM) for Unicode-encoded files.
  const blob = new Blob(["\uFEFF" + csvString], {
    type: "text/csv;charset=utf-8",
  });
  saveAs(blob, `${chartFileName ?? "chart_export"}.csv`);
};

const formatAsTime = (x): string => {
  const mins = x % 60;
  const hrs = Math.floor(x / 60);
  return `${hrs === 0 ? "00" : hrs}:${mins === 0 ? "00" : mins}`;
};

export const formatChartDataToCsv = (chartData) => {
  const {
    data,
    layout: {
      chartType,
      barmode,
      isWaterFallChart,
      xaxis: {
        title: { text: xAxisText },
        type: xAxisTitleType,
      },
    },
  } = chartData;

  if (barmode === "stack" && isWaterFallChart) {
    const labelNames = data[0].x;
    let csvData = [labelNames];
    let rowData: number[] = [];
    data.forEach((d, index) => {
      if (index >= 1) {
        rowData.push(d.y[index - 1]);
      }
    });
    csvData.push(rowData);
    return csvData;
  }

  const xAxisTitle = xAxisTitleType === "category" ? "Period " : xAxisText;
  const csv = new CSV(xAxisTitle, chartType, xAxisTitleType);

  data.forEach((d) => {
    d.x.forEach((x, i) => {
      csv.addCell(x, d.y[i], d.name, d.chart);
    });
  });

  return csv.getCSVArrayOfArrays();
};

export const canExportCsv = (data: Partial<ChartDataType>[]) =>
  data[0]?.type ? allowCsvType.includes(data[0].type) : false;
